import { ICartItem } from '@/api/types/cart';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { formatPriceNumberWithSymbol } from '@/utils/price';

interface UsePriceCalculationProps {
  cartItemData: ICartItem;
  currency?: any;
  selected?: boolean;
  itemPromotion?: any;
}

interface PriceInfo {
  price: number;
  sell_price: number;
  discount?: boolean;
}

export const usePriceCalculation = ({
  cartItemData,
  currency,
  selected = false,
  itemPromotion,
}: UsePriceCalculationProps) => {
  // 格式化价格
  const formatPrice = (price: number | string) => {
    return formatPriceNumberWithSymbol(String(price), currency);
  };

  // 根据币种获取价格信息
  const getPriceByCurrency = (priceList: any[], currencyName: string): PriceInfo => {
    if (!priceList || !Array.isArray(priceList)) return { price: 0, sell_price: 0 };
    const priceItem = priceList.find((item: any) => item.currency === currencyName) as any;
    if (priceItem) {
      const result: PriceInfo = {
        price: Number(priceItem.price || 0),
        sell_price: Number(priceItem.sell_price || priceItem.price || 0),
      };

      if (result.sell_price && result.price !== result.sell_price) {
        result.discount = true;
      }

      return result;
    }

    return { price: 0, sell_price: 0 };
  };

  // 检查是否有处方
  const hasPrescription = () => {
    const prescriptionType = cartItemData.option?.prescription_type;
    return (
      prescriptionType &&
      prescriptionType !== EPrescriptionType.NON_PRESCRIPTION &&
      prescriptionType !== EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION &&
      prescriptionType !== EPrescriptionType.FRAME_ONLY
    );
  };

  // 获取商品价格（仅商品本身，不包含促销逻辑）
  const getProductPrice = (): PriceInfo => {
    try {
      // 如果商品被选中且有促销信息，使用促销价格
      if (selected && itemPromotion) {
        return {
          price: Number(itemPromotion.product_original_price || 0),
          sell_price: Number(itemPromotion.product_price || 0),
        };
      }

      // 否则使用商品的正常价格
      if (cartItemData.product?.price_info) {
        return getPriceByCurrency(cartItemData.product.price_info, currency?.name);
      }

      return { price: 0, sell_price: 0 };
    } catch (error) {
      logger.error('Error getting product price:', error);
      return { price: 0, sell_price: 0 };
    }
  };

  // 获取镜片价格
  const getLensPrice = (): PriceInfo => {
    try {
      if (!cartItemData.lens) return { price: 0, sell_price: 0 };

      // 如果有处方，使用镜片类型价格
      if (hasPrescription() && cartItemData.lens.lens_type_price) {
        return getPriceByCurrency(cartItemData.lens.lens_type_price, currency?.name);
      }

      // 如果无处方，使用lens_price（已包含折射率价格）
      if (cartItemData.lens.lens_price) {
        return getPriceByCurrency(cartItemData.lens.lens_price, currency?.name);
      }

      return { price: 0, sell_price: 0 };
    } catch (error) {
      logger.error('Error getting lens price:', error);
      return { price: 0, sell_price: 0 };
    }
  };

  // 获取折射率价格
  const getIndexPrice = (): PriceInfo => {
    try {
      if (!cartItemData.lens?.index_price) return { price: 0, sell_price: 0 };

      return getPriceByCurrency(cartItemData.lens.index_price, currency?.name);
    } catch (error) {
      logger.error('Error getting index price:', error);
      return { price: 0, sell_price: 0 };
    }
  };

  // 获取膜层价格
  const getCoatingPrice = (): PriceInfo => {
    try {
      if (!cartItemData.coating?.price_info) return { price: 0, sell_price: 0 };

      return getPriceByCurrency(cartItemData.coating.price_info, currency?.name);
    } catch (error) {
      logger.error('Error getting coating price:', error);
      return { price: 0, sell_price: 0 };
    }
  };

  // 获取单价（不含数量）
  const getUnitPrice = (): PriceInfo => {
    try {
      // 如果商品被选中且有促销信息，使用促销总价
      if (selected && itemPromotion) {
        const total =
          Number(itemPromotion.product_payable || 0) +
          Number(itemPromotion.product_rule_discounts_price || 0);
        return {
          price: total,
          sell_price: total,
        };
      }

      // 否则累加各部分价格
      const productPrice = getProductPrice();
      const lensPrice = getLensPrice();
      // 只有在有处方的情况下才计算折射率价格
      const indexPrice = hasPrescription() ? getIndexPrice() : { price: 0, sell_price: 0 };
      const coatingPrice = getCoatingPrice();

      const totalPrice =
        productPrice.sell_price +
        lensPrice.sell_price +
        indexPrice.sell_price +
        coatingPrice.sell_price;
      const totalOriginalPrice =
        productPrice.price + lensPrice.price + indexPrice.price + coatingPrice.price;

      return {
        price: totalOriginalPrice,
        sell_price: totalPrice,
        discount: totalPrice !== totalOriginalPrice,
      };
    } catch (error) {
      logger.error('Error calculating unit price:', error);
      return { price: 0, sell_price: 0 };
    }
  };

  // 获取总价（含数量）
  const getTotalPrice = (): PriceInfo => {
    try {
      const unitPrice = getUnitPrice();
      const qty = cartItemData.qty || 1;

      // 如果商品被选中且有促销信息，直接使用促销价格，不需要乘以数量
      if (selected && itemPromotion) {
        return {
          price: unitPrice.price,
          sell_price: unitPrice.sell_price,
          discount: unitPrice.discount,
        };
      }

      // 否则正常乘以数量
      return {
        price: unitPrice.price * qty,
        sell_price: unitPrice.sell_price * qty,
        discount: unitPrice.discount,
      };
    } catch (error) {
      logger.error('Error calculating total price:', error);
      return { price: 0, sell_price: 0 };
    }
  };

  // 显示降价信息
  const showDownPrice = (): boolean => {
    const downPrice = getDownPrice();
    return downPrice > 0;
  };

  // 获取降价金额
  const getDownPrice = (): number => {
    try {
      // 如果商品被选中且有促销信息，使用促销的原价对比
      if (itemPromotion) {
        const { product_info: { original_price = [] } = {} } = itemPromotion;
        const originalPriceObj = original_price.find(
          (item: any) => item.currency === currency?.name,
        );
        const currentPrice = getProductPrice();

        if (originalPriceObj && currentPrice) {
          const diff = Number(originalPriceObj.price || 0) - Number(currentPrice.sell_price || 0);
          return Math.max(0, diff); // 确保不会返回负数
        }
      }

      // 否则使用商品的 original_price 与当前售价对比
      const productWithOriginalPrice = cartItemData.product as any;
      if (productWithOriginalPrice?.original_price) {
        const originalPriceObj = productWithOriginalPrice.original_price.find(
          (item: any) => item.currency === currency?.name,
        );
        const currentPrice = getProductPrice();

        if (originalPriceObj) {
          const diff = Number(originalPriceObj.price || 0) - Number(currentPrice.sell_price || 0);
          return Math.max(0, diff); // 确保不会返回负数
        }
      }

      return 0;
    } catch (error) {
      logger.error('Error calculating down price:', error);
      return 0;
    }
  };

  // 获取处方相关价格总和（镜片+折射率+镀膜）
  const getPrescriptionPrice = (): PriceInfo => {
    try {
      const lensPrice = getLensPrice();
      // 只有在有处方的情况下才计算折射率价格
      const indexPrice = hasPrescription() ? getIndexPrice() : { price: 0, sell_price: 0 };
      const coatingPrice = getCoatingPrice();

      const totalSellPrice = lensPrice.sell_price + indexPrice.sell_price + coatingPrice.sell_price;
      const totalOriginalPrice = lensPrice.price + indexPrice.price + coatingPrice.price;

      return {
        price: totalOriginalPrice,
        sell_price: totalSellPrice,
        discount: totalSellPrice !== totalOriginalPrice,
      };
    } catch (error) {
      logger.error('Error calculating prescription price:', error);
      return { price: 0, sell_price: 0 };
    }
  };

  // 获取所有计算好的价格信息
  const getAllPrices = () => {
    return {
      product: getProductPrice(),
      lens: getLensPrice(),
      index: getIndexPrice(),
      coating: getCoatingPrice(),
      prescription: getPrescriptionPrice(),
      unit: getUnitPrice(),
      total: getTotalPrice(),
    };
  };

  return {
    // 基础方法
    formatPrice,
    getPriceByCurrency,
    hasPrescription,

    // 价格获取方法
    getProductPrice,
    getLensPrice,
    getIndexPrice,
    getCoatingPrice,
    getPrescriptionPrice,
    getUnitPrice,
    getTotalPrice,
    getAllPrices,

    // 价格展示方法
    showDownPrice,
    getDownPrice,

    // 获取商品总价
    getItemTotalPrice: () => getTotalPrice().sell_price,
  };
};
